import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import GavelIcon from "@mui/icons-material/Gavel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { PQRSD_SLA_DAYS, PQRSD_TYPES } from "../../constants";

import { pqrsdPublicPath, pqrsdText } from "./pqrsdLabel";
const TYPE_DESCRIPTIONS = {
  peticion_general:
    "Solicitud respetuosa para que la entidad actúe o se pronuncie sobre un asunto concreto.",
  consulta:
    "Pregunta dirigida a la entidad sobre materias relacionadas con sus funciones.",
  info:
    "Solicitud de acceso a información o documentos públicos producidos o en custodia de la entidad.",
  queja:
    "Manifestación de inconformidad por la conducta o actuación irregular de un servidor.",
  reclamo:
    "Exigencia de la correcta prestación o suspensión indebida de un servicio.",
  denuncia:
    "Puesta en conocimiento de hechos presuntamente irregulares para su investigación.",
  sugerencia:
    "Propuesta para mejorar la gestión o prestación de los servicios.",
  felicitacion:
    "Reconocimiento por una actuación o servicio bien prestado.",
};

const STEPS = [
  {
    icon: <ArticleOutlinedIcon color="primary" />,
    title: "1. Diligencia el formulario",
    text:
      "Indica si deseas radicar de forma anónima, tus datos de contacto, el tipo de PQRSD y el detalle de tu solicitud. Puedes adjuntar archivos si lo necesitas.",
  },
  {
    icon: <FingerprintIcon color="primary" />,
    title: "2. Recibes un radicado",
    text:
      "Al enviar la solicitud se genera un número de radicado único (por ejemplo PQR_20260424-00059). Es tu comprobante y la llave para consultar el estado.",
  },
  {
    icon: <MarkEmailReadOutlinedIcon color="primary" />,
    title: "3. Confirmación por correo",
    text:
      "Recibirás un correo con el radicado y el enlace para hacer seguimiento al trámite. Guárdalo en un lugar seguro.",
  },
  {
    icon: <CheckCircleOutlineIcon color="primary" />,
    title: "4. Revisión y respuesta",
    text:
      "Un funcionario revisa y da trámite dentro de los términos legales. La respuesta te llegará al correo registrado y podrás descargarla desde la consulta de estado.",
  },
];

export default function PqrsdLanding() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1">
          {pqrsdText("PQRSD")}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Peticiones, Quejas, Reclamos, Sugerencias y Denuncias
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 680, mt: 2 }}>
          Este es el canal oficial para que la ciudadanía se comunique con la
          entidad. Antes de radicar, toma un momento para entender qué tipo de
          solicitud aplica a tu caso, los tiempos de respuesta y cómo
          protegemos tu información.
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="center"
        sx={{ mb: 5 }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(pqrsdPublicPath("radicar"))}
        >
          {pqrsdText("Radicar PQRSD")}
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<SearchOutlinedIcon />}
          onClick={() => navigate(pqrsdPublicPath("estado"))}
        >
          Consultar estado
        </Button>
      </Stack>

      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <GavelIcon color="primary" />
          <Typography variant="h5" component="h2">
            Tipos de solicitud y tiempos de respuesta
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Los términos están contados en días hábiles, siguiendo la Ley 1755 de
          2015. Si la respuesta requiere más tiempo, la entidad lo informará
          antes del vencimiento.
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(PQRSD_TYPES).map(([key, label]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: "nowrap",
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {PQRSD_SLA_DAYS[key]} días háb.
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {TYPE_DESCRIPTIONS[key]}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <EmailOutlinedIcon color="primary" />
          <Typography variant="h5" component="h2">
            Qué sucede después de radicar
          </Typography>
        </Stack>
        <List>
          {STEPS.map((step) => (
            <ListItem key={step.title} alignItems="flex-start" disableGutters>
              <ListItemIcon sx={{ mt: 0.5 }}>{step.icon}</ListItemIcon>
              <ListItemText
                primary={step.title}
                secondary={pqrsdText(step.text)}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <LockOutlinedIcon color="primary" />
          <Typography variant="h5" component="h2">
            Protección de tus datos
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ mb: 1 }}>
          La información que registres se usa únicamente para dar trámite a tu
          solicitud. Tus datos están protegidos y son tratados conforme a la
          política de tratamiento de datos personales de la entidad.
        </Typography>
        <Typography variant="body2">
          Si prefieres no compartir tus datos personales, puedes marcar la
          opción <strong>"Radicar de forma anónima"</strong> en el formulario.
          Ten en cuenta que en ese caso no podremos enviarte la respuesta
          directamente a un documento de identificación, pero sí al correo
          electrónico que indiques.
        </Typography>
      </Paper>

      <Alert severity="info" sx={{ mb: 4 }}>
        {pqrsdText("Al enviar una PQRSD declaras que la información suministrada es veraz. El suministro de datos falsos puede acarrear las sanciones previstas en la ley.")}
      </Alert>

      <Divider sx={{ my: 3 }} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="center"
      >
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(pqrsdPublicPath("radicar"))}
        >
          {pqrsdText("Estoy listo, radicar PQRSD")}
        </Button>
        <Button
          variant="text"
          size="large"
          onClick={() => navigate(pqrsdPublicPath("estado"))}
        >
          {pqrsdText("Consultar el estado de una PQRSD")}
        </Button>
      </Stack>
    </Container>
  );
}
